import {
	type BackendPlacementMessage,
	type FrontendPlacementMessage,
	noValidation,
} from '@post-office/placement-common-v2';
import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--growth/confluence-side-nav';
import { useHandleDismiss } from '@post-office/message-templates-common--growth-starburst/hooks/use-handle-dismiss';
import { TwcSampleBanner } from '@atlassian/twc-sample-banner';

import { type MessageTemplateContext } from '@post-office/message-template--twc-sidenav-banner';
import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import { getOnboardingUrl, resolveRedirectTo } from '../../../utils/get-onboarding-url';
import {
	type CreatePlacementMessageConfig,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
	type AnyFrontendStage,
} from '@post-office/placement-common-v2/create-placement/types';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';

type MessageFrontendProps = MessageTemplateContext & {
	handleOnDismissed: () => void;
	onboardingUrl: string;
	messageInstanceId: string;
	locale: string;
	redirectTo: 'jira' | 'confluence';
};

// Custom backend that fetches TCS hostname and TAP site trait to build the Confluence onboarding URL
export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: noValidation(),
		stage: () => ({
			goalMetadata: {
				primaryGoal: {
					goalId: 'clickthrough',
				},
			},
		}),
	},
	hydrate: {
		validate: assertMessageTemplateContext,
		stage: async ({ message, request }) => {
			const { logger, orchestrationContext, query, statsig } = request;
			const { messageInstanceId, messageTemplateId, triggerId } = message;
			const { cloudId: tenantId } = message.context;
			const { locale } = orchestrationContext;
			const redirectTo = resolveRedirectTo(message.context.redirectTo);

			if (!tenantId || typeof tenantId !== 'string') {
				logger.error(
					{ messageInstanceId, messageTemplateId, triggerId },
					'Tenant ID not available',
				);
				request.metrics
					.counter('twc-sidenav-banner.conf.error.count', { reason: 'tenant-id-not-available' })
					.incr();
				throw new Error('Tenant ID not available');
			}

			const isEnabled = statsig.getExperimentValue(
				{ identifiers: { tenantId } },
				{ name: 'twcg-991-jtbd-onboarding', parameter: 'isEnabled', defaultValue: false },
			);

			if (!isEnabled) {
				logger.info(
					{ messageInstanceId, messageTemplateId, triggerId, tenantId },
					'Control cohort - abort hydration',
				);
				request.metrics
					.counter('twc-sidenav-banner.conf.error.count', { reason: 'control-cohort' })
					.incr();
				throw Error('Control cohort - abort hydration');
			}

			logger.info(
				{ messageTemplateId, messageInstanceId, triggerId, orchestrationContext, query },
				'Hydrating twc-sidenav-banner (in-app-v2/confluence-side-nav)',
			);

			const onboardingUrl = await getOnboardingUrl({
				redirectTo,
				requestContext: request,
				messageInstanceId,
				messageTemplateId,
				orgId: message.context.orgId as string | undefined,
				twcEntitlementId: message.context.twcEntitlementId as string | undefined,
				triggerId: triggerId as string | undefined,
			});

			if (!onboardingUrl) {
				logger.error(
					{ messageTemplateId, messageInstanceId, triggerId, redirectTo },
					'No onboarding URL found',
				);
				request.metrics
					.counter('twc-sidenav-banner.conf.error.count', { reason: 'no-onboarding-url' })
					.incr();
				throw new Error('No onboarding URL found');
			}

			request.metrics.counter('twc-sidenav-banner.conf.success.count').incr();

			return {
				...message.context,
				redirectTo,
				onboardingUrl,
				messageInstanceId,
				locale,
			};
		},
	},
}));

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: ({
			redirectTo,
			handleOnDismissed,
			onboardingUrl,
			messageInstanceId,
			locale,
		}: MessageFrontendProps) => {
			const { handleDismiss } = useHandleDismiss({
				onDismissed: handleOnDismissed,
				clickIdentifier: 'twc-sidenav-banner-dismiss',
			});

			return (
				<TwcSampleBanner
					locale={locale}
					product={redirectTo}
					onClose={handleDismiss}
					onTryNowClick={handleDismiss}
					href={onboardingUrl}
					messageInstanceId={messageInstanceId}
				/>
			);
		},
	},
});
